import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Image as ImageIcon, ArrowLeft, X, ChevronLeft, ChevronRight, User, Instagram, Globe, Calendar } from "lucide-react";



import { SidebarAds } from "./BoxAd";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const PhotoDetailPage = () => {
  const { id } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, [id]);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API}/galleries/${id}`);
      setGallery(res.data);
    } catch (err) {
      console.error("Error fetching gallery:", err);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  
  const nextImage = () => {
    if (gallery && lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex + 1) % gallery.images.length);
    }
  };
  
  const prevImage = () => {
    if (gallery && lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex - 1 + gallery.images.length) % gallery.images.length);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, gallery]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#050505]">
        
        
        <div className="pt-8 flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
        </div>
      </main>
    );
  }

  if (!gallery) {
    return (
      <main className="min-h-screen bg-[#050505]">
        
        
        <div className="pt-8 flex flex-col items-center justify-center min-h-[60vh]">
          <ImageIcon className="w-16 h-16 text-zinc-700 mb-4" />
          <h1 className="font-heading text-2xl text-white mb-2">Gallery Not Found</h1>
          <p className="text-zinc-500 mb-6">This gallery may have been removed or is unavailable.</p>
          <Link
            to="/photos"
            className="flex items-center gap-2 text-white hover:text-orange-500"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Photos
          </Link>
        </div>
        
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#050505]" data-testid="photo-detail-page">
      
      

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-zinc-900 to-[#050505] border-b border-white/5 pt-8 pb-8">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          {/* Back Link */}
          <Link
            to="/photos"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Photos</span>
          </Link>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div>
              {gallery.category && (
                <span className="text-orange-500 text-sm font-bold uppercase tracking-wider mb-2 block">
                  {gallery.category}
                </span>
              )}
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white mb-4">
                {gallery.title}
              </h1>
              
              {gallery.event_name && (
                <p className="text-zinc-400 text-lg mb-2">{gallery.event_name}</p>
              )}
              
              <div className="flex items-center gap-4 text-zinc-500 text-sm">
                <span>{gallery.images?.length || 0} {gallery.images?.length === 1 ? "photo" : "photos"}</span>
                {gallery.event_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(gallery.event_date + "T00:00:00").toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
              
              {gallery.description && (
                <p className="text-zinc-400 mt-4 max-w-2xl">{gallery.description}</p>
              )}
            </div>

            {/* Photographer Credit */}
            {gallery.photographer && (
              <div className="flex items-center gap-4 bg-zinc-900/50 border border-white/5 p-4">
                <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden">
                  {gallery.photographer.profile_photo_url ? (
                    <img
                      src={gallery.photographer.profile_photo_url}
                      alt={gallery.photographer.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-6 h-6 text-zinc-600" />
                  )}
                </div>
                <div>
                  <p className="text-zinc-500 text-xs uppercase">Photographer</p>
                  <p className="text-white font-bold">{gallery.photographer.name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    {gallery.photographer.instagram && (
                      <a
                        href={`https://instagram.com/${gallery.photographer.instagram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zinc-500 hover:text-white"
                      >
                        <Instagram className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Photos Grid with Sidebar */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {!gallery.images || gallery.images.length === 0 ? (
              <div className="text-center py-16">
                <ImageIcon className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No photos in this gallery yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {gallery.images.map((image, index) => (
                  <motion.div
                    key={image.id || index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.03 }}
                    className="group cursor-pointer"
                    onClick={() => openLightbox(index)}
                  >
                    <div className="aspect-square bg-zinc-900 overflow-hidden">
                      <img
                        src={image.url}
                        alt={image.caption || `Photo ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    {image.caption && (
                      <p className="text-zinc-500 text-xs mt-2 line-clamp-1">{image.caption}</p>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar with Box Ads */}
          <aside className="hidden lg:block w-[300px] shrink-0">
            <div className="sticky top-32">
              <SidebarAds count={2} />
            </div>
          </aside>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && gallery.images && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 z-10 p-2 text-white hover:text-orange-500 transition-colors"
              onClick={closeLightbox}
            >
              <X className="w-8 h-8" />
            </button>

            {/* Navigation */}
            <button
              className="absolute left-4 z-10 p-2 text-white hover:text-orange-500 transition-colors"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
            >
              <ChevronLeft className="w-10 h-10" />
            </button>
            <button
              className="absolute right-4 z-10 p-2 text-white hover:text-orange-500 transition-colors"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
            >
              <ChevronRight className="w-10 h-10" />
            </button>

            {/* Image */}
            <div className="max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              <img
                src={gallery.images[lightboxIndex].url}
                alt={gallery.images[lightboxIndex].caption || `Photo ${lightboxIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain"
              />
              {gallery.images[lightboxIndex].caption && (
                <p className="text-white text-center mt-4">
                  {gallery.images[lightboxIndex].caption}
                </p>
              )}
              <p className="text-zinc-500 text-center text-sm mt-2">
                {lightboxIndex + 1} / {gallery.images.length}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      
    </main>
  );
};

export default PhotoDetailPage;
